import React, { useEffect, useState } from "react";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { IllustrationCarpool, IllustrationRoles, IllustrationPayment } from "./PriceIllustrations";

const STORAGE_KEY = "price_onboarding_seen_v1";

// Path prefixes where Price onboarding is welcome. On Saor-only routes
// (hotel dashboard, hotel/airbnb register) we don't want to show the
// covoiturage slides. In the native Price app there are no Saor routes,
// so `/` also qualifies.
const PRICE_PATHS = ["/", "/login", "/register", "/register/client", "/register/driver", "/dashboard/client", "/dashboard/driver"];

function isPricePath(pathname) {
  if (!pathname) return false;
  if (pathname === "/") return true;
  return PRICE_PATHS.some((p) => p !== "/" && pathname.startsWith(p));
}

function isCapacitorNative() {
  try {
    // Capacitor sets window.Capacitor.isNativePlatform() when running
    // inside the native iOS/Android WebView.
    return !!(typeof window !== "undefined" && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch {
    return false;
  }
}

/**
 * Price — Onboarding animé au premier lancement.
 *
 * Trois slides mobile-first qui expliquent :
 *   1. Price = covoiturage (pas VTC) — partage de trajets entre
 *      particuliers.
 *   2. Deux rôles complémentaires — passager vs covoitureur, avec les
 *      flows live et planifié.
 *   3. Paiement flexible — cash direct ou carte + IBAN via Stripe.
 *
 * Persistance : flag localStorage `price_onboarding_seen_v1` posé au
 * "Commencer". Un second lancement skip l'onboarding.
 */
const SLIDES = [
  {
    key: "welcome",
    accent: "#FF3B3B",
    bg: "#FFE5E5",
    Illustration: IllustrationCarpool,
    kicker: "Bienvenue sur Price",
    title: "Le covoiturage, pas le VTC",
    body: "Partage tes trajets entre particuliers, divise les frais et roule plus vert. Ici on partage la route ET le prix — chaque passager paye sa part, chaque covoitureur reste indépendant.",
    highlights: [
      "Trajets entre particuliers uniquement",
      "Prix partagé équitablement",
      "Pas d'employeur, pas de tarif imposé",
    ],
  },
  {
    key: "roles",
    accent: "#2A9D3F",
    bg: "#D6F5D6",
    Illustration: IllustrationRoles,
    kicker: "Choisis ton rôle",
    title: "Passager ou covoitureur ?",
    body: "Passager : cherche un covoitureur en 30 secondes ou publie ta demande live. Covoitureur : accepte des demandes live à la volée ou publie tes trajets planifiés à l'avance.",
    highlights: [
      "Live : demande immédiate façon Uber",
      "Planifié : réservation de siège à l'avance",
      "Notation étoile après chaque trajet",
    ],
  },
  {
    key: "payment",
    accent: "#FFB800",
    bg: "#FFF3CC",
    Illustration: IllustrationPayment,
    kicker: "Paiement flexible",
    title: "Cash ou carte, on gère tout",
    body: "Paye en espèces le jour du trajet ou par carte instantanément. Les covoitureurs reçoivent leur part directement sur leur IBAN via Stripe, et gardent un portefeuille pour les commissions cash.",
    highlights: [
      "Cash direct entre particuliers",
      "Paiement carte sécurisé Stripe",
      "IBAN covoitureur crédité auto",
    ],
  },
];

export default function PriceOnboarding() {
  const [visible, setVisible] = useState(false);
  const [idx, setIdx] = useState(0);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
      // On native (iOS/Android Price app) we always show. On web, we
      // only trigger on Price-relevant paths to avoid interrupting Saor
      // VTC users on the shared codebase.
      const path = (typeof window !== "undefined" && window.location && window.location.pathname) || "/";
      if (isCapacitorNative() || isPricePath(path)) {
        setVisible(true);
      }
    } catch {
      /* SSR-safe no-op */
    }
  }, []);

  if (!visible) return null;

  const isLast = idx === SLIDES.length - 1;
  const slide = SLIDES[idx];
  const Illustration = slide.Illustration;

  const next = () => {
    if (isLast) {
      try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch {/*noop*/}
      setVisible(false);
      return;
    }
    setIdx((v) => v + 1);
    setAnimKey((k) => k + 1);
  };

  const back = () => {
    if (idx === 0) return;
    setIdx((v) => v - 1);
    setAnimKey((k) => k + 1);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#FFF8F1] flex flex-col overflow-hidden"
      data-testid="price-onboarding"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
      }}
    >
      {/* Slide */}
      <div
        key={animKey}
        className="flex-1 flex flex-col justify-center px-5 sm:px-6 py-4 sm:py-8 animate-[slidein_0.35s_ease-out] overflow-y-auto"
        style={{
          animationName: "slidein",
        }}
      >
        <style>{`
          @keyframes slidein {
            0% { opacity: 0; transform: translateY(24px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes bounce_soft {
            0%, 100% { transform: translateY(0); }
            50%      { transform: translateY(-6px); }
          }
        `}</style>

        {/* Illustration — taille responsive : petits phones = 40, grands = 52 */}
        <div className="mx-auto mb-5 sm:mb-8 relative">
          <div
            className="w-40 h-40 sm:w-52 sm:h-52 rounded-[32px] sm:rounded-[36px] border-[3px] border-black shadow-[0_6px_0_0_#000] sm:shadow-[0_8px_0_0_#000] grid place-items-center relative overflow-hidden p-3"
            style={{
              backgroundColor: slide.bg,
              animation: "bounce_soft 3.2s ease-in-out infinite",
            }}
          >
            <Illustration accent={slide.accent} bg={slide.bg} />
          </div>
          {/* Cartoon "shadow" ring */}
          <div
            className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-32 sm:w-40 h-3 rounded-full opacity-25"
            style={{ backgroundColor: slide.accent }}
          />
        </div>

        {/* Copy */}
        <div className="text-center px-2">
          <div
            className="inline-block px-3 py-1 rounded-full border-2 border-black text-[10px] font-[Outfit] font-black uppercase tracking-[0.15em] mb-2 sm:mb-3"
            style={{ backgroundColor: slide.bg, color: "#000" }}
          >
            {slide.kicker}
          </div>
          <h1 className="font-[Outfit] text-[22px] sm:text-[28px] leading-[1.05] font-black text-black mb-2 sm:mb-3">
            {slide.title}
          </h1>
          <p className="font-[Outfit] text-[13px] sm:text-[15px] leading-[1.45] sm:leading-[1.5] text-neutral-700 max-w-[420px] mx-auto">
            {slide.body}
          </p>
        </div>

        {/* Highlights */}
        <div className="mt-4 sm:mt-6 space-y-2 max-w-[380px] mx-auto w-full">
          {slide.highlights.map((h, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-3 py-1.5 sm:py-2 rounded-2xl bg-white border-2 border-black shadow-[0_3px_0_0_#000]"
            >
              <div
                className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg border-2 border-black grid place-items-center shrink-0"
                style={{ backgroundColor: slide.accent }}
              >
                <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" strokeWidth={3.5} />
              </div>
              <span className="font-[Outfit] text-[12px] sm:text-[13px] font-bold text-black">{h}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom nav */}
      <div className="px-5 sm:px-6 pb-5 sm:pb-8 pt-3 sm:pt-4 border-t-2 border-black bg-white">
        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mb-3 sm:mb-4">
          {SLIDES.map((s, i) => (
            <button
              key={s.key}
              type="button"
              data-testid={`onboarding-dot-${i}`}
              onClick={() => { setIdx(i); setAnimKey((k) => k + 1); }}
              className={
                "h-2 rounded-full border border-black transition-all " +
                (i === idx ? "w-8" : "w-2 bg-white")
              }
              style={i === idx ? { backgroundColor: slide.accent } : {}}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-3">
          {idx > 0 && (
            <button
              type="button"
              data-testid="onboarding-back"
              onClick={back}
              aria-label="Retour"
              className="h-12 sm:h-14 w-12 sm:w-14 shrink-0 rounded-2xl bg-white text-black font-[Outfit] font-black border-2 border-black shadow-[0_4px_0_0_#000] active:translate-y-[2px] active:shadow-[0_2px_0_0_#000] transition-all grid place-items-center"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={3} />
            </button>
          )}
          <button
            type="button"
            data-testid="onboarding-next"
            onClick={next}
            className="flex-1 h-12 sm:h-14 rounded-2xl text-white font-[Outfit] font-black text-[14px] sm:text-[15px] uppercase tracking-wider border-2 border-black shadow-[0_4px_0_0_#000] active:translate-y-[2px] active:shadow-[0_2px_0_0_#000] transition-all flex items-center justify-center gap-2"
            style={{ backgroundColor: slide.accent }}
          >
            {isLast ? "Commencer" : "Continuer"}
            <ArrowRight className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>
      </div>
    </div>
  );
}
